// The ring surface (docs/06 §2, web/plan.md): the Android-parity incoming-call
// look — a large mercury disc, the caller's name, an "incoming call" caption,
// and round decline(red)/accept(green) buttons with ink glyphs. It is shared by
// two mounts: the full-screen `/incoming` route (compact + standalone) and the
// desktop AppShell's modal overlay. Presentation only — it owns no engine,
// navigation, or audio; IncomingCallScreen wires those and passes resolved
// props. Keeping it pure is exactly what lets it mount both ways.

import { type ReactNode } from 'react';
import { MercuryAvatar } from '../../avatar/MercuryAvatar.js';
import { useT } from '../../i18n/index.js';

export type RingVariant = 'fullscreen' | 'modal';

export interface RingSurfaceProps {
  /** Resolved caller name, or a generic title during the provisional cold open. */
  title: string;
  /** Status line under the title, e.g. "is calling" / "Connecting…". */
  caption: string;
  /**
   * Composed avatar seed (candidateSeed(userId, avatarSeed)). Omitted when the
   * caller isn't known yet (provisional) or has no avatar — a neutral disc shows
   * instead. MercuryAvatar is the ONLY draw path; this never renders pixels of
   * its own.
   */
  avatarSeed?: string;
  /** Initial letter for the neutral disc when the caller is known but has no
   *  avatar seed. Omitted during the provisional cold open (generic caller). */
  initial?: string;
  /** Accept was tapped/queued: swap the receiver glyph for a spinner. */
  accepting?: boolean;
  onAccept: () => void;
  onDecline: () => void;
  variant?: RingVariant;
}

// Big enough to read as "the caller" across the pane or a phone screen.
const AVATAR_SIZE = 128;

export function RingSurface({
  title,
  caption,
  avatarSeed,
  initial,
  accepting = false,
  onAccept,
  onDecline,
  variant = 'fullscreen',
}: RingSurfaceProps): ReactNode {
  const t = useT();

  const body = (
    <>
      <section className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        {avatarSeed ? (
          <MercuryAvatar seed={avatarSeed} size={AVATAR_SIZE} className="rounded-full" />
        ) : (
          <div
            aria-hidden="true"
            className="flex items-center justify-center rounded-full bg-raised font-semibold text-muted"
            style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, fontSize: AVATAR_SIZE * 0.4 }}
          >
            {initial ?? ''}
          </div>
        )}
        <h1 className="text-3xl font-semibold text-ink">{title}</h1>
        <p role="status" className="text-lg text-muted">
          {caption}
        </p>
      </section>
      <section
        aria-label={t('incoming.answerControls')}
        className="flex items-center justify-around pb-8"
      >
        <button
          type="button"
          aria-label={t('common.decline')}
          onClick={onDecline}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-danger text-ink"
        >
          <HangUpIcon />
        </button>
        <button
          type="button"
          aria-label={t('common.accept')}
          aria-busy={accepting || undefined}
          onClick={onAccept}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-ok text-ink"
        >
          {accepting ? <Spinner /> : <PhoneIcon />}
        </button>
      </section>
    </>
  );

  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('incoming.incomingCall')}
          className="flex min-h-[26rem] w-full max-w-sm flex-col rounded-3xl border border-edge bg-surface p-6 shadow-2xl"
        >
          {body}
        </div>
      </div>
    );
  }

  return <main className="mx-auto flex min-h-full w-full max-w-app flex-col p-6">{body}</main>;
}

// --- Answer icons (inline SVG; emoji render inconsistently across OEM fonts).
// The red decline glyph mirrors InCallScreen's HangUpIcon; accept is an upright
// receiver so the two read as opposites at a glance. Both glyphs are ink. ------

function HangUpIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" className="h-8 w-8" fill="currentColor" aria-hidden="true">
      <path d="M12 10.5c-3.9 0-7.4 1.4-9.7 3.7a1 1 0 0 0 0 1.4l2 2a1 1 0 0 0 1.4 0l1.9-1.6c.3-.2.4-.6.4-.9v-2a12.6 12.6 0 0 1 8 0v2c0 .3.1.7.4.9l1.9 1.6a1 1 0 0 0 1.4 0l2-2a1 1 0 0 0 0-1.4c-2.3-2.3-5.8-3.7-9.7-3.7z" />
    </svg>
  );
}

function PhoneIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" className="h-8 w-8" fill="currentColor" aria-hidden="true">
      <path d="M6.6 10.8c1.4 2.8 3.8 5.2 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.3 1z" />
    </svg>
  );
}

function Spinner(): ReactNode {
  return (
    <span
      aria-hidden="true"
      className="h-7 w-7 animate-spin rounded-full border-2 border-ink border-t-transparent"
    />
  );
}
